/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

/** All supported icon names for ModernIcon. */
export type ModernIconName =
    | 'check'
    | 'tent-tree'
    | 'ticket'
    | 'id-card'
    | 'trees'
    | 'warehouse'
    | 'search'
    | 'x'
    | 'arrow-left'
    | 'map-pin'
    | 'mouse'
    | 'mouse-off';

/** SVG children (paths, circles, rects) per icon. Shared SVG wrapper is in ModernIcon. */
const ICON_PATHS: Record<ModernIconName, React.ReactNode> = {
    check: <path d="M20 6 9 17l-5-5" />,
    'tent-tree': (
        <>
            <circle cx="4" cy="4" r="2" />
            <path d="m14 5 3-3 3 3" />
            <path d="m14 10 3-3 3 3" />
            <path d="M17 14V2" />
            <path d="M17 14H7l-5 8h20Z" />
            <path d="M8 14v8" />
            <path d="m9 14 5 8" />
        </>
    ),
    ticket: (
        <>
            <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
            <path d="M13 5v2" />
            <path d="M13 17v2" />
            <path d="M13 11v2" />
        </>
    ),
    'id-card': (
        <>
            <path d="M16 10h2" />
            <path d="M16 14h2" />
            <path d="M6.17 15a3 3 0 0 1 5.66 0" />
            <circle cx="9" cy="11" r="2" />
            <rect x="2" y="5" width="20" height="14" rx="2" />
        </>
    ),
    trees: (
        <>
            <path d="M10 10v.2A3 3 0 0 1 8.9 16H5a3 3 0 0 1-1-5.8V10a3 3 0 0 1 6 0Z" />
            <path d="M7 16v6" />
            <path d="M13 19v3" />
            <path d="M12 19h8.3a1 1 0 0 0 .7-1.7L18 14h.3a1 1 0 0 0 .7-1.7L16 9h.2a1 1 0 0 0 .8-1.7L13 3l-1.4 1.5" />
        </>
    ),
    warehouse: (
        <>
            <path d="M18 21V10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1v11" />
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 1.132-1.803l7.95-3.974a2 2 0 0 1 1.837 0l7.948 3.974A2 2 0 0 1 22 8z" />
            <path d="M6 13h12" />
            <path d="M6 17h12" />
        </>
    ),
    search: (
        <>
            <path d="m21 21-4.34-4.34" />
            <circle cx="11" cy="11" r="8" />
        </>
    ),
    x: (
        <>
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
        </>
    ),
    'arrow-left': (
        <>
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
        </>
    ),
    'map-pin': (
        <>
            <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
            <circle cx="12" cy="10" r="3" />
        </>
    ),
    mouse: (
        <>
            <rect x="5" y="2" width="14" height="20" rx="7" />
            <path d="M12 6v4" />
        </>
    ),
    'mouse-off': (
        <>
            <path d="M12 6v.343" />
            <path d="M18.218 18.218A7 7 0 0 1 5 15V9a7 7 0 0 1 .782-3.218" />
            <path d="M19 13.343V9A7 7 0 0 0 8.56 2.902" />
            <path d="M22 22 2 2" />
        </>
    ),
};

export interface ModernIconProps extends Omit<
    React.SVGProps<SVGSVGElement>,
    'width' | 'height'
> {
    iconName: ModernIconName;
    size?: number;
    strokeWidth?: number;
    absoluteStrokeWidth?: boolean;
}

/**
 * Single shared icon component. Renders one SVG wrapper with icon-specific path
 * content. Use size (not width/height) so absoluteStrokeWidth scales correctly.
 */
export function ModernIcon({
    iconName,
    size = 24,
    strokeWidth = 2,
    absoluteStrokeWidth = false,
    ...rest
}: ModernIconProps) {
    const computedStrokeWidth = absoluteStrokeWidth
        ? strokeWidth * (size / 24)
        : strokeWidth;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={computedStrokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            {...rest}
        >
            {ICON_PATHS[iconName] ?? null}
        </svg>
    );
}
